import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getAllNotifications,
  updateNotificationViewStatus,
} from "../../services/api";
import { initializeWebSocket } from "../../services/websocketConfig";
import { NotificationDto } from "../../types/types";
import {
  NotificationsContext,
  NotificationsContextValue,
} from "./notificationsContext";

// Safari on iPhone has no Notification unless the site is added to the home
// screen, so check for it first. Without the check the whole page crashes.
const browserNotificationsAvailable =
  typeof window !== "undefined" && "Notification" in window;

const countUnread = (list: NotificationDto[]): number =>
  list.filter((notification) => !notification.viewStatus).length;

const showBrowserNotification = (notification: NotificationDto): void => {
  if (
    !browserNotificationsAvailable ||
    notification.viewStatus ||
    Notification.permission !== "granted"
  ) {
    return;
  }

  const displayName =
    notification.from.firstName && notification.from.lastName
      ? `${notification.from.firstName} ${notification.from.lastName}`
      : notification.from.firstName || notification.from.email || "Someone";

  try {
    new Notification(`New notification from ${displayName}`, {
      body: notification.message,
      icon: notification.from.profileImage || "/favicon.ico",
    });
  } catch {
    // Chrome on Android allows notifications only from a service worker.
    // The Notifications page still shows the notification.
  }
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [pendingIds, setPendingIds] = useState<ReadonlySet<number>>(new Set());
  const pendingIdsRef = useRef<Set<number>>(new Set());
  const notificationDaysRef = useRef<number>(7);

  const refreshNotifications = useCallback(async (days?: number): Promise<void> => {
    if (typeof days === "number") {
      notificationDaysRef.current = days;
    }
    try {
      const response: NotificationDto[] = await getAllNotifications(notificationDaysRef.current);
      if (response) {
        setNotifications(response);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, []);

  const toggleReadStatus = useCallback(
    async (notificationId: number): Promise<void> => {
      // The backend flips the status on every call, so a second click while
      // the first is still saving would undo it.
      if (pendingIdsRef.current.has(notificationId)) {
        return;
      }

      const flipStatus = (items: NotificationDto[]): NotificationDto[] =>
        items.map((item) =>
          item.id === notificationId ? { ...item, viewStatus: !item.viewStatus } : item
        );

      pendingIdsRef.current.add(notificationId);
      setPendingIds(new Set(pendingIdsRef.current));
      setNotifications(flipStatus);

      try {
        await updateNotificationViewStatus(notificationId);
      } catch (error) {
        console.error("Error updating notification status:", error);
        setNotifications(flipStatus);
      } finally {
        pendingIdsRef.current.delete(notificationId);
        setPendingIds(new Set(pendingIdsRef.current));
      }
    },
    []
  );

  useEffect(() => {
    refreshNotifications();

    const { cleanup } = initializeWebSocket({
      onMessageReceived: showBrowserNotification,
      onFetchNotifications: () => refreshNotifications(),
    });

    if (browserNotificationsAvailable && Notification.permission === "default") {
      Promise.resolve(Notification.requestPermission()).catch(() => {});
    }

    return cleanup;
  }, [refreshNotifications]);

  const value = useMemo<NotificationsContextValue>(
    () => ({
      notifications,
      unreadCount: countUnread(notifications),
      pendingIds,
      refreshNotifications,
      toggleReadStatus,
    }),
    [notifications, pendingIds, refreshNotifications, toggleReadStatus]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};
